import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { ItemStatus, updateUrlWithException, dateToString } from "../../Utils";
import { fetchWorkOrders } from "../../../api/work-order";
import { CompactModuleFilter } from "../../utils/RelatedEntries/CompactModuleFilter";
import { RelatedEntries } from "../../utils/RelatedEntries/RelatedEntries";
import { ModuleFilter } from "../../utils/ModuleFilter";
import { getUsersAsync } from "../../../slices/user";
import { WORK_ORDER_STATUS_INCLUSION } from "./utils";
import { WorkOrderBulkCreateOrderDialog } from "./work-order-bulk-create-order-dialog";
import { TableToolbar } from "../../utils/TableToolbar";
import { useTableView } from "../../utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../utils/PMTableView/RightDrawer";
import {
  WorkOrdersDataTable,
  getWorkOrderColumns,
  responsibleLabel,
  executorLabel,
} from "./work-orders-table";
import {
  computeHasMore,
  scopeFiltersKey,
} from "../../utils/PMTableView/embedded-table-utils";

export const WorkOrderStacklist = (props) => {
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  return <WorkOrderStacklistContent key={JSON.stringify([companyId, props.module, props.filters])} {...props} />;
};

const WorkOrderStacklistContent = (props) => {
  const { onStatsChange } = props;
  const embedded = Boolean(props.module);
  const Filter = embedded ? CompactModuleFilter : ModuleFilter;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const usersState = useSelector((s) => s.users);
  const currentUser = useSelector((s) => s.session.currentUser);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);
  const [drawerItem, setDrawerItem] = useState(null);

  const scopeFilters = props?.filters || [];
  const scopeKey = scopeFiltersKey(scopeFilters);
  const loadMoreInFlightRef = useRef(false);
  const requestSequence = useRef(0);
  useEffect(() => () => { requestSequence.current += 1; }, []);

  const [controller, setController] = useState({
    filters: [...(props?.filters || [])],
    page: searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1,
    per_page: searchParams.get("per_page")
      ? parseInt(searchParams.get("per_page"), 10)
      : 25,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: embedded ? "desc" : searchParams.get("sort") || "desc",
    sortBy: embedded ? "created_at" : searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "updated_at",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
  });

  const getInitialFilters = () => {
    const query = searchParams.get("query") || "";
    const statusesParam = searchParams.get("statuses");
    const executorsParam = searchParams.get("executors");
    let statuses = [];
    let users = [];

    if (statusesParam) {
      try {
        statuses = JSON.parse(statusesParam);
      } catch (e) {
        statuses = [];
      }
    }

    if (executorsParam) {
      try {
        users = JSON.parse(executorsParam);
      } catch (e) {
        users = [];
      }
    }

    return {
      query,
      statuses,
      users: Array.isArray(users) ? users : [],
      invoiced: false,
      finished: false,
    };
  };

  const [filters, setFilters] = useState(getInitialFilters);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const STACKLIST_MERGE_FIELDS = useMemo(
    () => [
      "created_at",
      "number",
      "title",
      "status",
      "order_date",
      "due_date",
      "customer",
      "project",
      "is_completed",
      "order",
      "sales_income_included_in_project",
      "responsible_executor_participant",
      "participants",
    ],
    []
  );

  const effectiveFieldsParam = useMemo(() => {
    if (!props?.fields) return undefined;
    const set = new Set(
      String(props.fields)
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    );
    STACKLIST_MERGE_FIELDS.forEach((f) => set.add(f));
    return Array.from(set).join(",");
  }, [props?.fields, STACKLIST_MERGE_FIELDS]);

  const canUseBulkOrderTool = useMemo(() => {
    const roleNames = (currentUser?.roles || []).map((r) => r.name);
    if (roleNames.includes("admin")) return true;
    return roleNames.includes("projects_work-orders_edit");
  }, [currentUser]);

  const canSelectWorkOrderForBulk = useCallback(
    (item) => {
      if (!item?.id) return false;
      if (!item.is_completed) return false;
      if (item.sales_income_included_in_project) return false;
      if (["cancelled", "invoiced", "draft", "ongoing"].includes(item.status))
        return false;
      if (item.order?.id) return false;
      const roleNames = (currentUser?.roles || []).map((r) => r.name);
      if (roleNames.includes("admin")) return true;
      if (!roleNames.includes("projects_work-orders_edit")) return false;
      const role = item.role_for_current_user;
      return ["RESPONSIBLE", "RESPONSIBLE_EXECUTOR"].includes(role);
    },
    [currentUser]
  );

  const customCheckboxButtonFilters = [
    {
      key: "invoiced",
      label: t("Invoiced"),
      icon: DocumentTextIcon,
      defaultValue: false,
    },
    {
      key: "finished",
      label: t("Finished"),
      icon: CheckCircleIcon,
      defaultValue: false,
    },
  ];

  useEffect(() => {
    if (embedded) return;
    updateUrlWithException(
      {
        query: filters.query,
        statuses:
          filters.statuses.length > 0 ? JSON.stringify(filters.statuses) : null,
        executors:
          filters.users?.length > 0 ? JSON.stringify(filters.users) : null,
        page: controller.page,
        per_page: controller.per_page,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
      },
      "filters"
    );
  }, [
    embedded,
    filters,
    controller.page,
    controller.per_page,
    controller.sort,
    controller.sortBy,
    controller.view,
  ]);

  const buildController = useCallback(
    (page) => {
      const filtersArray = [];

      const wantsClosed = Boolean(filters?.finished || filters?.invoiced);
      if (filters?.statuses && filters.statuses.length > 0) {
        filtersArray.push({
          name: "status",
          value: JSON.stringify(filters.statuses),
        });
      } else if (wantsClosed) {
        const statuses = [
          "draft",
          "ongoing",
          "processing",
          "ready_invoice",
          "approved",
          "updated",
          "rejected",
        ];
        if (filters?.finished) statuses.push("finished");
        if (filters?.invoiced) statuses.push("invoiced", "cancelled");
        filtersArray.push({
          name: "status",
          value: JSON.stringify(statuses),
        });
      }
      if (filters?.users?.length > 0) {
        filtersArray.push({
          name: "responsible_executor_id",
          value: JSON.stringify(filters.users.map((u) => String(u))),
        });
      }

      const allFilters = [...scopeFilters, ...filtersArray];

      return {
        ...controller,
        page,
        query: filters?.query || "",
        filters: allFilters,
        ...(wantsClosed && !(filters?.statuses && filters.statuses.length)
          ? { include_closed: true }
          : {}),
        ...(effectiveFieldsParam && { fields: effectiveFieldsParam }),
        ...(props?.per_page && { per_page: props.per_page }),
      };
    },
    [controller, filters, scopeKey, effectiveFieldsParam, props?.per_page]
  );

  const getWorkOrders = useCallback(
    async (page = 1, append = false) => {
      const request = ++requestSequence.current;
      setIsLoading(true);
      setError(false);
      try {
        const response = await fetchWorkOrders(buildController(page));
        if (request !== requestSequence.current) return false;
        if (response.status !== 200) {
          throw new Error(`Server error: ${response.status}`);
        }
        if (!Array.isArray(response?.data?.data)) throw new Error("Invalid work order list");
        const rows = response.data.data;
        setData((prev) => {
          if (!append) return rows;
          const existing = new Set(prev.map((d) => d.id));
          return [...prev, ...rows.filter((r) => !existing.has(r.id))];
        });
        setTotalCount(
          typeof response?.data?.totalCount === "number"
            ? response.data.totalCount
            : 0
        );
        setCurrentCount(
          typeof response?.data?.currentCount === "number"
            ? response.data.currentCount
            : rows.length
        );
        return true;
      } catch (err) {
        if (request === requestSequence.current) setError(true);
        return false;
      } finally {
        if (request === requestSequence.current) setIsLoading(false);
      }
    },
    [buildController]
  );

  const fetchKey = useMemo(
    () =>
      JSON.stringify({
        scopeKey,
        filters,
        sort: controller.sort,
        sortBy: controller.sortBy,
        effectiveFieldsParam,
        per_page: props?.per_page || controller.per_page,
      }),
    [
      scopeKey,
      filters,
      controller.sort,
      controller.sortBy,
      effectiveFieldsParam,
      props?.per_page,
      controller.per_page,
    ]
  );

  // Re-fetch (page 1) whenever filters/sort change — not on page changes (load more)
  useEffect(() => {
    setController((c) => (c.page === 1 ? c : { ...c, page: 1 }));
    getWorkOrders(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  useEffect(() => {
    onStatsChange?.({ totalCount, currentCount });
  }, [totalCount, currentCount, onStatsChange]);

  useEffect(() => {
    dispatch(
      getUsersAsync({
        filters: [{ name: "employee_or_hired", value: true }],
        page: 1,
        per_page: 200,
        query: "",
        sort: "asc",
        sortBy: "name",
        view: "all",
        fields: "id,displayTitle,name",
      })
    );
  }, [dispatch]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setController((c) => ({ ...c, page: 1 }));
  };

  const executorUserOptions = (usersState?.data || []).map((u) => ({
    value: String(u.id),
    label: u.displayTitle || u.name || String(u.id),
  }));

  // ── Table view state ───────────────────────────────────────────────────────
  const columns = useMemo(() => getWorkOrderColumns(t), [t]);
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "work_orders",
    columns: columnMeta,
    getSnapshot: () => ({
      query: filters.query,
      statuses: filters.statuses,
      users: filters.users,
      invoiced: filters.invoiced,
      finished: filters.finished,
      sort: controller.sort,
      sortBy: controller.sortBy,
    }),
    applySnapshot: (snap) => {
      setFilters((f) => ({
        ...f,
        query: snap.query || "",
        statuses: snap.statuses || [],
        users: snap.users || [],
        invoiced: !!snap.invoiced,
        finished: !!snap.finished,
      }));
      setController((c) => ({
        ...c,
        page: 1,
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handleLoadMore = useCallback(() => {
    const loaded = data?.length || 0;
    const total = totalCount || 0;
    if (loadMoreInFlightRef.current || isLoading || loaded >= total) return;

    const nextPage = (controller.page || 1) + 1;
    loadMoreInFlightRef.current = true;
    getWorkOrders(nextPage, true).then((loaded) => {
      if (loaded) setController((c) => ({ ...c, page: nextPage }));
    }).finally(() => {
      loadMoreInFlightRef.current = false;
    });
  }, [controller.page, data?.length, totalCount, isLoading, getWorkOrders]);

  // Bulk: only completed/eligible work orders may be converted to orders
  const eligibleSelected = useMemo(
    () => selectedItems.filter((item) => canSelectWorkOrderForBulk(item)),
    [selectedItems, canSelectWorkOrderForBulk]
  );
  const eligibleSelectedIds = useMemo(
    () => eligibleSelected.map((item) => Number(item.id)),
    [eligibleSelected]
  );

  const hasMore = computeHasMore({
    isLoading,
    dataLength: data?.length,
    totalCount,
  });

  return (
    <div className="min-w-0 lg:col-span-3 lg:col-start-1 sm:col-span-3">
      <div className="space-y-4">
        <Filter
          filterCard
          disabled={isLoading}
          onFiltersChange={handleFiltersChange}
          initialFilters={filters}
          acceptedStatuses={WORK_ORDER_STATUS_INCLUSION}
          searchPlaceholder={t("Search work orders...")}
          filterTitle={t("Filter Work Orders by Status")}
          customCheckboxButtonFilters={customCheckboxButtonFilters}
          userOptions={executorUserOptions}
          usersFilterLabel={t("work_orders.filter_executor")}
        />

        {canUseBulkOrderTool && eligibleSelected.length > 0 ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm">
            <span className="text-sm font-medium text-amber-950">
              {t("work_orders.bulk_create.toolbar", {
                count: eligibleSelected.length,
              })}
            </span>
            <button
              type="button"
              className="rounded-lg border border-amber-800/30 bg-amber-900 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-950"
              onClick={() => setBulkDialogOpen(true)}
            >
              {t("work_orders.bulk_create.open")}
            </button>
          </div>
        ) : null}

        {embedded ? <RelatedEntries kind="workOrders" context={props.module} data={data} isLoading={isLoading} error={error} totalCount={totalCount}
          onRetry={() => { setController((value) => ({ ...value, page: 1 })); getWorkOrders(1, false); }} onRowClick={setDrawerItem} renderStatus={(item) => <ItemStatus item={item} />}
          hasMore={hasMore} onLoadMore={handleLoadMore} selectedItems={selectedItems} canSelect={canSelectWorkOrderForBulk} onSelectionChange={canUseBulkOrderTool ? setSelectedItems : undefined}
        /> : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {t("Something went wrong. Could not save data!")}
          </div>
        ) : (
          <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)]">
            <TableToolbar
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <WorkOrdersDataTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              data={data}
              isLoading={isLoading}
              density={tableView.density}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              onItemsSelect={(items) => setSelectedItems(items)}
              onRowClick={(item) => setDrawerItem(item)}
              layout="responsive"
            />
          </div>
        )}
      </div>

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerItem}
        onClose={() => setDrawerItem(null)}
        eyebrow={drawerItem ? `${t("Work Order #")} ${drawerItem.number || ""}` : ""}
        title={
          drawerItem?.title ||
          drawerItem?.displayTitle ||
          t("work_orders.table_no_title")
        }
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/work_orders/${drawerItem.id}`),
          },
        ]}
      >
        {drawerItem && (
          <DrawerSection>
            <DrawerProps
              items={[
                { label: t("status"), value: <ItemStatus item={drawerItem} /> },
                {
                  label: t("customer"),
                  value:
                    drawerItem.customer?.displayTitle ||
                    drawerItem.customer?.name ||
                    "—",
                },
                {
                  label: t("Project"),
                  value:
                    drawerItem.project?.displayTitle ||
                    drawerItem.project?.name ||
                    "—",
                },
                { label: t("Responsible"), value: responsibleLabel(drawerItem) },
                { label: t("Executor"), value: executorLabel(drawerItem) },
                {
                  label: t("Order Date"),
                  value: drawerItem.order_date
                    ? dateToString(drawerItem.order_date)
                    : "—",
                },
                {
                  label: t("Due Date"),
                  value: drawerItem.due_date
                    ? dateToString(drawerItem.due_date)
                    : "—",
                },
              ]}
            />
          </DrawerSection>
        )}
      </RightDrawer>

      <WorkOrderBulkCreateOrderDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        workOrderIds={eligibleSelectedIds}
        onDone={() => {
          setSelectedItems([]);
          getWorkOrders(1, false);
        }}
      />
    </div>
  );
};
